import {computed, Ref} from "vue";
import dayjs from "dayjs";
import useAgenda from "@/agenda/composables/useAgenda";

/**
 * Composable to fetch appointments based on a date range.
 *
 * A thin adapter over useAgenda, the single window surface since
 * GET healthcenter/appointments was retired. Delegating rather than fetching
 * on its own is what keeps the two consumers apart: both share the app-global SWR
 * cache, and fetching here once stored a bare array under the very key useAgenda
 * fills with its {key, appointments, reminders} payload — same month shown in
 * the agenda and in the patient timeline, identical from/to, one consumer
 * reading the other's shape. Going through useAgenda gives one key and one
 * shape, plus its stale-window guard and its live refresh.
 *
 * Canceled and refused appointments are left out by default, as the previous
 * `active` filter did.
 */
const useAppointments = (range: Ref<{
    startDate: Date | null;
    endDate: Date | null;
} | undefined>) => {
    /**
     * The agenda window. The calendar hands both bounds at midnight, and the
     * endpoint keeps what starts strictly before `to`: without the whole last
     * day, its appointments would be missing from the grid.
     */
    const agendaRange = computed(() => {
        if (!range?.value?.startDate || !range?.value.endDate) {
            return null;
        }

        return {
            from: range.value.startDate,
            to: dayjs(range.value.endDate).endOf('day').toDate()
        };
    });

    const {data, isLoading} = useAgenda(agendaRange);

    return {
        data,
        isLoading
    };
};

export default useAppointments;
